import { Injectable } from '@angular/core';

import { LocalStorageUtil } from '@shared/utils/local-storage/local-storage.util';

const UTM_STORAGE_KEY = 'dood_utm_params';
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface UtmPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

interface StoredUtm {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  storedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class UtmStorageService {
  /**
   * Reads UTM params from URL query and stores them in localStorage for 24h.
   * New values overwrite previous ones.
   */
  captureFromQueryParams(params: URLSearchParams | Record<string, string>): void {
    const source =
      typeof (params as URLSearchParams).get === 'function' ? (params as URLSearchParams) : null;
    const utmSource = source?.get('utm_source') ?? (params as Record<string, string>)['utm_source'];
    const utmMedium = source?.get('utm_medium') ?? (params as Record<string, string>)['utm_medium'];
    const utmCampaign =
      source?.get('utm_campaign') ?? (params as Record<string, string>)['utm_campaign'];

    const hasAny = !!(utmSource ?? utmMedium ?? utmCampaign);
    if (!hasAny) {
      return;
    }

    const stored: StoredUtm = {
      ...(utmSource && { utm_source: utmSource }),
      ...(utmMedium && { utm_medium: utmMedium }),
      ...(utmCampaign && { utm_campaign: utmCampaign }),
      storedAt: Date.now(),
    };
    LocalStorageUtil.set(UTM_STORAGE_KEY, stored);
  }

  /**
   * Returns stored UTM payload if still within 24h TTL, otherwise null (and clears storage if expired).
   * Only includes keys that were actually stored.
   */
  getUtmPayload(): Partial<UtmPayload> | null {
    const raw = LocalStorageUtil.get(UTM_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    let parsed: StoredUtm;
    try {
      parsed = JSON.parse(raw) as StoredUtm;
    } catch {
      LocalStorageUtil.remove(UTM_STORAGE_KEY);
      return null;
    }

    const now = Date.now();
    if (typeof parsed.storedAt !== 'number' || now - parsed.storedAt > TTL_MS) {
      LocalStorageUtil.remove(UTM_STORAGE_KEY);
      return null;
    }

    const hasAny = !!(parsed.utm_source ?? parsed.utm_medium ?? parsed.utm_campaign);
    if (!hasAny) {
      return null;
    }

    return {
      ...(parsed.utm_source && { utm_source: parsed.utm_source }),
      ...(parsed.utm_medium && { utm_medium: parsed.utm_medium }),
      ...(parsed.utm_campaign && { utm_campaign: parsed.utm_campaign }),
    };
  }
}
